import {
  MAP_GROUP_NAME_TO_MASTER_TYPE,
  type SimilarityThresholdType,
} from "../../ComparativeAnalysisPage.constants";
import {
  ALLOWED_METRIC_TYPES,
  ROWS_THRESHOLD_COLORS,
} from "./ComparativeAnalysisMetrics.constants";

export const calcDelta = (row: {
  base_value: number | null;
  compare_value: number | null;
}): number | undefined => {
  const v1 = row.base_value;
  const v2 = row.compare_value;

  if (v1 === null && v2 === null) {
    return undefined;
  }

  // only calculate delta if both values are numbers
  if (v1 === null && Number.isFinite(v2)) {
    return 100;
  }

  if (v2 === null && Number.isFinite(v1)) {
    return -100;
  }

  // Avoid division by zero
  if (v1 === 0) {
    if (v2 === 0) return 0;
    return undefined;
  }

  const delta = ((v2 - v1) / Math.abs(v1)) * 100;

  return delta;
  // return rounded delta
  // return Math.abs(delta) < 0.1 ? Math.round(delta) : delta;
};

export const isMetricWhitelistedForComparativeAnalysis = (row: {
  metric_type: string;
  isInGroup?: boolean;
}): boolean =>
  ALLOWED_METRIC_TYPES.includes(row.metric_type) ||
  row.metric_type.startsWith("custom.");

// TODO: the below interface should be in a single consistant place
interface Metric {
  isInGroup?: boolean;
  asset_values_list?: string[];
  base_value: number | null;
  compare_value: number | null;
  metric_type: string;
}

export const isMetricDeltaInThresholdRange = (
  delta: number | undefined,
  similarityThreshold: SimilarityThresholdType,
  metricType: string
): boolean => {
  const masterType = MAP_GROUP_NAME_TO_MASTER_TYPE[metricType];

  const thresholdsByType = similarityThreshold[masterType] || [-Infinity, Infinity];

  return delta >= thresholdsByType[0] && delta <= thresholdsByType[1];
};

// complex case
export const isMetricoutsideThresholds = (
  metric: Metric,
  metrics: Metric[],
  similarityThreshold: SimilarityThresholdType
): boolean => {
  const delta = calcDelta(metric);
  const allowed = isMetricWhitelistedForComparativeAnalysis(metric);

  // ignore these scenarios:
  // 1. metric is not whitelisted and is irrelevant for comparison
  // 2. delta is `undefined` (due to various reasons) and this metric is NOT an aggregated metric (a table row which expands to show sub-rows)
  if (!allowed || delta === undefined) {
    return undefined;
  }

  return !isMetricDeltaInThresholdRange(
    delta,
    similarityThreshold,
    metric.metric_group_type
  );
};

export const getRowClassName = (row, rows, similarityThreshold) => {
  const delta = calcDelta(row);
  const allowed = isMetricWhitelistedForComparativeAnalysis(row);

  if (!allowed || delta === undefined) {
    return "";
  }

  const metricOutsideThresholds = isMetricoutsideThresholds(
    row,
    rows,
    similarityThreshold
  );

  if (metricOutsideThresholds === undefined) {
    return "";
  }

  return metricOutsideThresholds
    ? ROWS_THRESHOLD_COLORS.BAD
    : ROWS_THRESHOLD_COLORS.GOOD;
};

export const getTableSx = () => ({
  fontSize: 14,
  border: "none",
  ".MuiDataGrid-toolbarContainer": {
    padding: 0,
  },
});
